/**
 * Marker Icons Configuration for Parcel Lookup
 * Creates custom SVG icons for different marker types
 */

// Import color configuration
import {
  getMarkerColor,
  getRecreationalAreaColor,
  getRecreationalAreaIcon,
} from '../config/markerColors';

/**
 * Creates a custom marker icon using SVG
 * @param {string} markerType - Type of marker (ingreso, etapa, bloque, etc.)
 * @param {string} iconColor - Color for the marker
 * @param {string} text - Text to display on the marker
 * @param {string} textColor - Color for the text
 * @returns {Object} Leaflet divIcon configuration
 */
export const createCustomMarkerIcon = (markerType, iconColor = '#FF0000', text = '', textColor = '#FFFFFF', imageUrl = null) => {
  let svgString = '';
  let iconSize = [60, 40]; // Default size
  let iconAnchor = [30, 40]; // Default anchor

  switch (markerType) {
    case 'ingreso':
      const ingresoText = text || 'ENTRADA';
      const ingresoWidth = Math.max(100, ingresoText.length * 8 + 24);
      svgString = createIngresoSVG(iconColor, textColor, ingresoText);
      iconSize = [ingresoWidth, 50];
      iconAnchor = [ingresoWidth / 2, 50];
      break;

    case 'etapa':
      svgString = createEtapaSVG(iconColor, textColor, text || 'ETAPA');
      iconSize = calculateIconSize(text || 'ETAPA');
      iconAnchor = [iconSize[0]/2, iconSize[1]];
      break;

    case 'bloque':
      svgString = createBloqueSVG(iconColor, textColor, text || 'BLOQUE');
      const bloqueText = text || 'BLOQUE';
      const bloqueWidth = Math.max(20, Math.min(80, bloqueText.length * 6 + 12));
      iconSize = [bloqueWidth, 20]; // Tamaño compacto
      iconAnchor = [bloqueWidth / 2, 0]; // Anchor centrado
      break;

    case 'estacionamiento':
      svgString = createEstacionamientoSVG(iconColor, textColor, text || 'Estacionamiento');
      iconSize = calculateIconSize(text || 'Estacionamiento');
      iconAnchor = [iconSize[0]/2, iconSize[1]];
      break;

    case 'area_verde':
      svgString = createAreaVerdeSVG(iconColor, textColor, text || 'Área Verde');
      iconSize = calculateIconSize(text || 'Área Verde');
      iconAnchor = [iconSize[0]/2, iconSize[1]];
      break;

    case 'salida':
      const salidaText = text || 'SALIDA';
      const salidaWidth = Math.max(100, salidaText.length * 8 + 24);
      svgString = createSalidaSVG(iconColor, textColor, salidaText);
      iconSize = [salidaWidth, 50];
      iconAnchor = [salidaWidth / 2, 50];
      break;

    case 'punto_interes':
      svgString = createPuntoInteresSVG(iconColor, textColor, text || 'Punto de Interés', imageUrl);
      iconSize = [60, 60]; // Tamaño fijo para pin con imagen
      iconAnchor = [30, 60]; // Anchor en la punta del pin
      break;

    case 'punto_foto':
      svgString = createPuntoFotoSVG(iconColor, textColor, imageUrl);
      iconSize = [40, 50]; // Mismo tamaño con o sin imagen
      iconAnchor = [20, 50];
      break;

    case 'arboles':
      svgString = createArbolesSVG();
      iconSize = [32, 40]; // Tamaño compacto para icono de árbol
      iconAnchor = [16, 40];
      break;

    default:
      svgString = createDefaultSVG(iconColor, textColor, text || 'Marker');
      iconSize = calculateIconSize(text || 'Marker');
      iconAnchor = [iconSize[0]/2, iconSize[1]];
  }

  return {
    html: svgString,
    className: 'custom-marker',
    iconSize: iconSize,
    iconAnchor: iconAnchor,
    popupAnchor: [0, -40] // Consistent popup position
  };
};

/**
 * Calculate icon size based on text length
 */
const calculateIconSize = (text, customHeight = 40) => {
  const textLength = text.length;
  const width = Math.max(60, textLength * 7 + 16);
  const height = customHeight;
  return [width, height];
};

/**
 * Creates SVG for Ingreso marker (similar to Etapa - square with arrow)
 */
const createIngresoSVG = (iconColor, textColor, text = 'ENTRADA') => {
  const textLength = text.length;
  const width = Math.max(100, textLength * 8 + 24);
  const height = 50;
  
  return `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow-entrada" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="rgba(0,0,0,0.3)"/>
        </filter>
      </defs>
      <!-- Square (cuadradito) similar a etapa -->
      <rect x="0" y="0" width="${width}" height="32" rx="4" fill="${iconColor}" filter="url(#shadow-entrada)"/>
      <text x="${width/2}" y="22" text-anchor="middle" fill="${textColor}" 
            font-family="Arial, sans-serif" font-size="12" font-weight="bold">${text}</text>
      <!-- Arrow down -->
      <polygon points="${width/2 - 4},32 ${width/2 + 4},32 ${width/2},42" fill="${iconColor}"/>
    </svg>
  `;
};

/**
 * Creates SVG for Etapa marker
 */
const createEtapaSVG = (iconColor, textColor, text) => {
  const textLength = text.length;
  const width = Math.max(80, textLength * 7 + 16);
  const height = 40;
  
  return `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.3)"/>
        </filter>
      </defs>
      <!-- Label -->
      <rect x="8" y="0" width="${width - 16}" height="28" rx="4" fill="${iconColor}" stroke="white" stroke-width="1" filter="url(#shadow)"/>
      <text x="${width/2}" y="18" text-anchor="middle" fill="${textColor}" font-family="Arial, sans-serif" font-size="12" font-weight="bold">${text}</text>
      <!-- Small pin -->
      <rect x="${width/2 - 1}" y="28" width="2" height="8" fill="${iconColor}" opacity="0.8"/>
    </svg>
  `;
};

/**
 * Creates SVG for Bloque marker
 */
const createBloqueSVG = (iconColor, textColor, text) => {
  const textLength = text.length;
  // Ancho más compacto: mínimo 20px, pero puede crecer según el texto
  const width = Math.max(20, Math.min(80, textLength * 6 + 12));
  const height = 20; // Altura más compacta
  
  return `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-color="rgba(0,0,0,0.2)"/>
        </filter>
      </defs>
      <!-- Box compacto -->
      <rect x="0" y="0" width="${width}" height="${height}" rx="4" fill="${iconColor}" stroke="#E5E7EB" stroke-width="1" filter="url(#shadow)"/>
      <text x="${width/2}" y="14" text-anchor="middle" fill="${textColor}" font-family="Arial, sans-serif" font-size="9" font-weight="bold">${text}</text>
    </svg>
  `;
};

/**
 * Creates SVG for Estacionamiento marker with parked car icon
 */
const createEstacionamientoSVG = (iconColor, textColor, text = 'Estacionamiento') => {
  const textLength = text.length;
  const width = Math.max(60, textLength * 7 + 16);
  const height = 40;
  
  return `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.3)"/>
        </filter>
        <filter id="softShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="1" stdDeviation="1" flood-color="rgba(0,0,0,0.2)"/>
        </filter>
      </defs>
      
      <!-- Background circle for better visibility -->
      <circle cx="${width/2}" cy="12" r="14" fill="#F8FAFC" stroke="#E2E8F0" stroke-width="1" filter="url(#softShadow)"/>
      
      <!-- Parked car icon -->
      <g transform="translate(${width/2 - 10}, 4)">
        <!-- Car body -->
        <rect x="2" y="8" width="16" height="8" rx="1" fill="#3B82F6" filter="url(#shadow)"/>
        
        <!-- Car roof -->
        <rect x="4" y="4" width="12" height="6" rx="1" fill="#3B82F6" filter="url(#shadow)"/>
        
        <!-- Windshield -->
        <rect x="5" y="5" width="10" height="4" rx="0.5" fill="#E5E7EB" opacity="0.8"/>
        
        <!-- Car windows -->
        <rect x="6" y="6" width="3" height="2" rx="0.3" fill="#93C5FD" opacity="0.6"/>
        <rect x="11" y="6" width="3" height="2" rx="0.3" fill="#93C5FD" opacity="0.6"/>
        
        <!-- Wheels -->
        <circle cx="5" cy="18" r="2" fill="#374151" filter="url(#softShadow)"/>
        <circle cx="15" cy="18" r="2" fill="#374151" filter="url(#softShadow)"/>
        
        <!-- Wheel centers -->
        <circle cx="5" cy="18" r="1" fill="#6B7280"/>
        <circle cx="15" cy="18" r="1" fill="#6B7280"/>
        
        <!-- Headlights -->
        <circle cx="3" cy="10" r="0.8" fill="#FEF3C7" opacity="0.9"/>
        <circle cx="17" cy="10" r="0.8" fill="#FEF3C7" opacity="0.9"/>
        
        <!-- Parking lines underneath -->
        <g transform="translate(0, 22)">
          <rect x="0" y="0" width="20" height="1" fill="${iconColor}" opacity="0.3"/>
          <rect x="0" y="2" width="20" height="1" fill="${iconColor}" opacity="0.3"/>
        </g>
      </g>
      
      <!-- Small pin -->
      <rect x="${width/2 - 1}" y="24" width="2" height="8" fill="${iconColor}" opacity="0.8"/>
    </svg>
  `;
};

/**
 * Creates SVG for Área Verde marker with detailed trees and plants icon
 */
const createAreaVerdeSVG = (iconColor, textColor, text = 'Área Verde') => {
  const textLength = text.length;
  const width = Math.max(60, textLength * 7 + 16);
  const height = 40;
  
  return `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.3)"/>
        </filter>
        <filter id="softShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="1" stdDeviation="1" flood-color="rgba(0,0,0,0.2)"/>
        </filter>
      </defs>
      
      <!-- Green area background -->
      <g transform="translate(${width/2 - 12}, 2)">
        <!-- Main tree -->
        <g transform="translate(8, 0)">
          <!-- Tree trunk -->
          <rect x="4" y="14" width="3" height="10" fill="#8B4513" filter="url(#softShadow)"/>
          
          <!-- Tree canopy - layered circles for depth -->
          <circle cx="5.5" cy="12" r="6" fill="#16A34A" filter="url(#shadow)"/>
          <circle cx="5.5" cy="10" r="5" fill="#22C55E" filter="url(#softShadow)"/>
          <circle cx="5.5" cy="8" r="4" fill="#4ADE80" filter="url(#softShadow)"/>
          
          <!-- Tree highlights -->
          <circle cx="4" cy="9" r="1.5" fill="#86EFAC" opacity="0.8"/>
          <circle cx="7" cy="11" r="1" fill="#86EFAC" opacity="0.8"/>
        </g>
        
        <!-- Smaller tree on the left -->
        <g transform="translate(2, 4)">
          <!-- Tree trunk -->
          <rect x="2" y="10" width="2" height="8" fill="#8B4513" filter="url(#softShadow)"/>
          
          <!-- Tree canopy -->
          <circle cx="3" cy="9" r="4" fill="#16A34A" filter="url(#softShadow)"/>
          <circle cx="3" cy="7" r="3" fill="#22C55E" filter="url(#softShadow)"/>
          <circle cx="3" cy="6" r="2.5" fill="#4ADE80" filter="url(#softShadow)"/>
        </g>
        
        <!-- Small bush/plant on the right -->
        <g transform="translate(16, 8)">
          <!-- Plant base -->
          <ellipse cx="2" cy="6" rx="3" ry="2" fill="#16A34A" filter="url(#softShadow)"/>
          <ellipse cx="2" cy="5" rx="2.5" ry="1.5" fill="#22C55E" filter="url(#softShadow)"/>
          <ellipse cx="2" cy="4" rx="2" ry="1" fill="#4ADE80" filter="url(#softShadow)"/>
          
          <!-- Small flowers/leaves -->
          <circle cx="1" cy="3" r="0.8" fill="#FFD700" opacity="0.8"/>
          <circle cx="3" cy="4" r="0.6" fill="#FFD700" opacity="0.8"/>
          <circle cx="2.5" cy="2" r="0.7" fill="#FFD700" opacity="0.8"/>
        </g>
        
        <!-- Ground/grass elements -->
        <g transform="translate(0, 20)">
          <!-- Grass tufts -->
          <path d="M3 0 Q3.5 -2 4 0 Q4.5 -1.5 5 0" stroke="#22C55E" stroke-width="1.5" fill="none" opacity="0.8"/>
          <path d="M8 0 Q8.5 -1.5 9 0 Q9.5 -2 10 0" stroke="#22C55E" stroke-width="1.5" fill="none" opacity="0.8"/>
          <path d="M15 0 Q15.5 -1.5 16 0 Q16.5 -2 17 0" stroke="#22C55E" stroke-width="1.5" fill="none" opacity="0.8"/>
        </g>
      </g>
    </svg>
  `;
};

/**
 * Creates SVG for Arboles marker - clean tree icon without text
 * Uses flat colors only (no gradients/filters) for reliable rendering
 */
const createArbolesSVG = () => {
  const width = 32;
  const height = 40;

  return `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
      <!-- Ground shadow -->
      <ellipse cx="16" cy="38" rx="6" ry="1.8" fill="#1a3a1a" opacity="0.18"/>
      <!-- Trunk -->
      <rect x="14.5" y="22" width="3" height="14" rx="1" fill="#6B4226"/>
      <rect x="15" y="22" width="1.5" height="14" rx="0.5" fill="#7D5332" opacity="0.6"/>
      <!-- Back canopy -->
      <circle cx="12" cy="17" r="6" fill="#1B8A3A"/>
      <circle cx="20" cy="17" r="6" fill="#1B8A3A"/>
      <!-- Main canopy -->
      <circle cx="16" cy="15" r="8" fill="#22A948"/>
      <!-- Mid layer -->
      <circle cx="13" cy="14" r="5.5" fill="#2EBD55"/>
      <circle cx="19" cy="15" r="5" fill="#2EBD55"/>
      <!-- Top highlights -->
      <circle cx="16" cy="12" r="5" fill="#45D46A"/>
      <circle cx="14" cy="10.5" r="3" fill="#6AE28B"/>
      <!-- Light spot -->
      <circle cx="13" cy="9.5" r="1.5" fill="#95EDAC" opacity="0.7"/>
    </svg>
  `;
};


/**
 * Creates SVG for Salida marker
 */
const createSalidaSVG = (iconColor, textColor, text = 'SALIDA') => {
  const textLength = text.length;
  const width = Math.max(100, textLength * 8 + 24);
  const height = 50;
  
  return `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow-salida" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="rgba(0,0,0,0.3)"/>
        </filter>
      </defs>
      <!-- Square (cuadradito) similar a etapa -->
      <rect x="0" y="0" width="${width}" height="32" rx="4" fill="${iconColor}" filter="url(#shadow-salida)"/>
      <text x="${width/2}" y="22" text-anchor="middle" fill="${textColor}" 
            font-family="Arial, sans-serif" font-size="12" font-weight="bold">${text}</text>
      <!-- Arrow down -->
      <polygon points="${width/2 - 4},32 ${width/2 + 4},32 ${width/2},42" fill="${iconColor}"/>
    </svg>
  `;
};

/**
 * Creates SVG for Punto de Interés marker with optional image
 */
const createPuntoInteresSVG = (iconColor, textColor, text = 'Punto de Interés', imageUrl = null) => {
  const width = 60;
  const height = 60;
  // Generate unique ID for this marker instance
  const uniqueId = `pin-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  return `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
      <defs>
        <filter id="shadow-punto-${uniqueId}" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="rgba(0,0,0,0.3)"/>
        </filter>
        <clipPath id="pin-clip-${uniqueId}">
          <circle cx="30" cy="18" r="14"/>
        </clipPath>
      </defs>
      <!-- Pin shape -->
      <path d="M30 0 C40 0, 48 8, 48 18 C48 28, 30 48, 30 48 C30 48, 12 28, 12 18 C12 8, 20 0, 30 0 Z" 
            fill="${iconColor}" filter="url(#shadow-punto-${uniqueId})"/>
      ${imageUrl ? `
        <!-- Image inside pin (circular) -->
        <circle cx="30" cy="18" r="14" fill="white" opacity="0.3"/>
        <image 
          x="16" 
          y="4" 
          width="28" 
          height="28" 
          href="${imageUrl}" 
          clip-path="url(#pin-clip-${uniqueId})"
          preserveAspectRatio="xMidYMid cover"
        />
        <!-- Small star icon overlay at bottom -->
        <circle cx="30" cy="40" r="3" fill="${textColor}" opacity="0.9"/>
        <path d="M30 37 L30.3 38.5 L31.8 38.5 L30.8 39.5 L31.2 41 L30 40.2 L28.8 41 L29.2 39.5 L28.2 38.5 L29.7 38.5 Z" 
              fill="${textColor}" opacity="0.9"/>
      ` : `
        <!-- Star icon (default when no image) -->
        <path d="M30 12 L31 18 L37 18 L32 22 L34 28 L30 24 L26 28 L28 22 L23 18 L29 18 Z" 
              fill="${textColor}"/>
      `}
    </svg>
  `;
};

/**
 * Creates SVG for Punto de Foto marker with optional image
 */
const createPuntoFotoSVG = (iconColor, textColor = '#FFFFFF', imageUrl = null) => {
  // Si hay imagen, mostrar un preview circular en el centro del pin
  if (imageUrl) {
    const uniqueId = `punto-foto-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    // Radio más pequeño para que la imagen quede en el centro y el pin sea visible
    const imageRadius = 18;
    return `
      <svg width="40" height="50" viewBox="0 0 40 50" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <clipPath id="clip-${uniqueId}">
            <circle cx="20" cy="20" r="${imageRadius}"/>
          </clipPath>
          <filter id="shadow-fotos-${uniqueId}" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="rgba(0,0,0,0.3)"/>
          </filter>
        </defs>
        <!-- Pin shape (forma del marcador) -->
        <path d="M20 0 C30 0, 38 8, 38 18 C38 28, 20 48, 20 48 C20 48, 2 28, 2 18 C2 8, 10 0, 20 0 Z" 
              fill="${iconColor}" filter="url(#shadow-fotos-${uniqueId})"/>
        <!-- Image preview circular en el centro del pin -->
        <circle cx="20" cy="20" r="${imageRadius}" fill="url(#image-${uniqueId})" clip-path="url(#clip-${uniqueId})" stroke="${iconColor}" stroke-width="2"/>
        <defs>
          <pattern id="image-${uniqueId}" x="0" y="0" width="100%" height="100%">
            <image href="${imageUrl}" x="0" y="0" width="${imageRadius * 2}" height="${imageRadius * 2}" preserveAspectRatio="xMidYMid slice"/>
          </pattern>
        </defs>
      </svg>
    `;
  }
  
  // Sin imagen, mostrar icono de cámara
  return `
    <svg width="40" height="50" viewBox="0 0 40 50" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow-fotos" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="rgba(0,0,0,0.3)"/>
        </filter>
      </defs>
      <!-- Pin shape -->
      <path d="M20 0 C30 0, 38 8, 38 18 C38 28, 20 48, 20 48 C20 48, 2 28, 2 18 C2 8, 10 0, 20 0 Z" 
            fill="${iconColor}" filter="url(#shadow-fotos)"/>
      <!-- Camera icon -->
      <rect x="12" y="16" width="16" height="12" rx="2" fill="none" stroke="${textColor}" stroke-width="2"/>
      <circle cx="20" cy="22" r="3" fill="none" stroke="${textColor}" stroke-width="1.5"/>
      <path d="M14 16 L16 14 L18 14 L20 16" fill="none" stroke="${textColor}" stroke-width="1.5" stroke-linecap="round"/>
    </svg>
  `;
};

/**
 * Creates SVG for default marker
 */
const createDefaultSVG = (iconColor, textColor, text) => {
  const textLength = text.length;
  const width = Math.max(60, textLength * 7 + 16);
  const height = 40;
  
  return `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.3)"/>
        </filter>
      </defs>
      <!-- Label -->
      <rect x="8" y="0" width="${width - 16}" height="24" rx="4" fill="${iconColor}" filter="url(#shadow)"/>
      <text x="${width/2}" y="16" text-anchor="middle" fill="${textColor}" font-family="Arial, sans-serif" font-size="12" font-weight="bold">${text}</text>
      <!-- Small pin -->
      <rect x="${width/2 - 1}" y="24" width="2" height="8" fill="${iconColor}" opacity="0.8"/>
    </svg>
  `;
};

/**
 * Marker type configurations with Mapbox-inspired colors
 */
export const MARKER_TYPES = {
  ingreso: {
    id: 'ingreso',
    name: 'Ingreso',
    color: getMarkerColor('entrada'),
    textColor: '#FFFFFF',
    editableText: false,
    defaultText: 'ENTRADA'
  },
  salida: {
    id: 'salida',
    name: 'Salida',
    color: getMarkerColor('salida'),
    textColor: '#FFFFFF',
    editableText: false,
    defaultText: 'SALIDA'
  },
  etapa: {
    id: 'etapa',
    name: 'Etapa',
    color: getMarkerColor('etapa'),
    textColor: '#FFFFFF',
    editableText: true,
    defaultText: 'NUEVA ETAPA'
  },
  bloque: {
    id: 'bloque',
    name: 'Bloque',
    color: getMarkerColor('bloque'),
    textColor: '#6B7280',
    editableText: true,
    defaultText: 'BLOQUE'
  },
  estacionamiento: {
    id: 'estacionamiento',
    name: 'Estacionamiento',
    color: getRecreationalAreaColor('Estacionamiento'),
    textColor: '#FFFFFF',
    editableText: false,
    defaultText: 'Estacionamiento'
  },
  area_verde: {
    id: 'area_verde',
    name: 'Área Verde',
    color: getRecreationalAreaColor('Área Verde'),
    textColor: '#FFFFFF',
    editableText: false,
    defaultText: 'Área Verde'
  },
  arboles: {
    id: 'arboles',
    name: 'Árboles',
    color: getMarkerColor('arboles'),
    textColor: '#FFFFFF',
    editableText: false,
    defaultText: 'Zona de Árboles'
  },
  punto_interes: {
    id: 'punto_interes',
    name: 'Punto de Interés',
    color: getMarkerColor('punto_interes'),
    textColor: '#FFFFFF',
    editableText: false,
    defaultText: 'Punto de Interés'
  },
  punto_foto: {
    id: 'punto_foto',
    name: 'Punto de Foto',
    color: getMarkerColor('punto_foto'),
    textColor: '#FFFFFF',
    editableText: false,
    defaultText: 'Punto de Foto'
  },
  default: {
    id: 'default',
    name: 'Marcador',
    color: getMarkerColor('default'),
    textColor: '#FFFFFF',
    editableText: false,
    defaultText: 'Marcador'
  }
};

/**
 * Get color for recreational area type
 * Uses the new Mapbox-inspired color palette
 */
export const getColorForRecreationalArea = (type) => {
  return getRecreationalAreaColor(type);
};

/**
 * Get icon emoji for recreational area type
 */
export const getIconForRecreationalArea = (type) => {
  return getRecreationalAreaIcon(type);
};
